import {AuthorizeEsiaResponsePayload} from '../../../../shared/models/api/authorizeEsiaResponsePayload';
import {GetHolderProfileCabinetResponsePayload} from '../../../../shared/models/api';
import {RegisterClientCabinetRequestPayload} from '../../../../shared/models/api/registerClientCabinetRequestPayload';
import {EsiaResponseType} from '../../../../shared/models/login';
import {Sex} from '../../../../shared/models/profile/sex';
import {formatPhone, formatSnils, onlyDigitsPipe} from '../../../../shared/pipes';
import {KidSaturated, ParentSaturated} from '../esiaRegCardState/model';
import {EsiaRegCardForm, KidSubform} from './form/model';

const formatMobilePhone = (phone?: string | null): string | null => {
  if (!phone) return null;

  let digits = onlyDigitsPipe(phone);

  if (digits.length === 10 && digits[0] === '9') {
    digits = `7${digits}`;
  } else if (digits.length === 11 && digits[0] === '8') {
    digits = `7${digits.slice(1)}`;
  }

  return digits.length === 11 && digits[0] === '7' ? `+${digits}` : phone;
};

export const mapHolderProfileToEsiaRegInfo = (
  holder: GetHolderProfileCabinetResponsePayload,
): AuthorizeEsiaResponsePayload => ({
  type: EsiaResponseType.Registration,
  token: null,
  firstName: holder.firstName ?? null,
  lastName: holder.lastName ?? null,
  middleName: holder.middleName ?? null,
  birthDate: holder.birthDate ?? null,
  birthPlace: null,
  gender: (holder.sex as Sex) ?? null,
  address: holder.address ?? null,
  mobilePhone: formatMobilePhone(holder.mobilePhone),
  email: holder.email ?? null,
  num: null,
  issueDate: null,
  issueId: null,
  issuedBy: null,
  addressReg: null,
  snils: holder.snils ?? null,
  cardStatusId: holder.cardStatusId,
  kids: null,
});

export const mapEsiaRegInfoToFormValues = (
  regInfo: Partial<
    AuthorizeEsiaResponsePayload & {
      isEmailNotificationEnabled: boolean;
      isSmsNotificationEnabled: boolean;
      isAccountLinkingEnabled: boolean;
    }
  >,
): EsiaRegCardForm => ({
  firstName: regInfo.firstName ?? '',
  lastName: regInfo.lastName ?? '',
  middleName: regInfo.middleName ?? '',
  birthDate: regInfo.birthDate ?? '',
  birthPlace: regInfo.birthPlace ?? '',
  sex: regInfo.gender ?? Sex.male,
  address: regInfo.address ?? '',
  mobilePhone: formatPhone(regInfo.mobilePhone ?? '').replace('+7', '8'),
  email: regInfo.email ?? '',
  number: regInfo.num ?? '',
  date: regInfo.issueDate ?? '',
  codeOrg: regInfo.issueId ?? '',
  org: (regInfo.issuedBy ?? '').replace('выдан', '').trim(),
  addressReg: regInfo.addressReg ?? '',
  snils: formatSnils(onlyDigitsPipe(regInfo.snils ?? '')),
  isEmailNotificationEnabled: regInfo.isEmailNotificationEnabled,
  isSmsNotificationEnabled: regInfo.isSmsNotificationEnabled,
  isAccountLinkingEnabled: regInfo.isAccountLinkingEnabled ?? true,
});
export const mapFormValuesToEsiaRegInfo = (
  formData: Partial<EsiaRegCardForm>,
  accessToken: string | null,
  extras?: Pick<AuthorizeEsiaResponsePayload, 'cardStatusId' | 'cardApplicationStatusId' | 'cardDeliveryInfo'>,
): AuthorizeEsiaResponsePayload => ({
  firstName: formData.firstName ?? '',
  lastName: formData.lastName ?? '',
  middleName: formData.middleName ?? '',
  birthDate: formData.birthDate ?? '',
  birthPlace: formData.birthPlace ?? '',
  gender: formData.sex ?? Sex.male,
  address: formData.address ?? '',
  mobilePhone: formatPhone(formData.mobilePhone ?? '').replace('8', '+7'),
  email: formData.email ?? '',
  num: formData.number ?? '',
  issueDate: formData.date ?? '',
  issueId: formData.codeOrg ?? '',
  issuedBy: formData.org?.trim() ?? '',
  addressReg: formData.addressReg ?? '',
  snils: formatSnils(onlyDigitsPipe(formData.snils ?? '')),
  type: EsiaResponseType.Registration,
  token: accessToken,
  cardStatusId: extras?.cardStatusId,
  cardApplicationStatusId: extras?.cardApplicationStatusId,
  cardDeliveryInfo: extras?.cardDeliveryInfo,
  isEmailNotificationEnabled: formData.isEmailNotificationEnabled,
  isSmsNotificationEnabled: formData.isSmsNotificationEnabled,
  kids: [],
});

export const mapFormValuesToEsiaRegData = (
  formData: EsiaRegCardForm | KidSubform,
  accessToken?: string | null,
): Omit<RegisterClientCabinetRequestPayload, 'counterpartyDepartmentId' | 'deliveryTypeId' | 'desiredAddress'> => {
  const values: EsiaRegCardForm = formData;
  const {isEmailNotificationEnabled, isSmsNotificationEnabled, ...otherValues} = values;

  return {
    accessToken,
    ...otherValues,
    ...(isEmailNotificationEnabled === undefined ? {} : {isEmailNotificationEnabled}),
    ...(isSmsNotificationEnabled === undefined ? {} : {isSmsNotificationEnabled}),
    email: formData.email || null,
    mobilePhone: formData.mobilePhone ? onlyDigitsPipe(formData.mobilePhone) : null,
    addressReg: formData.addressReg || null,
    snils: onlyDigitsPipe(formData.snils),
  };
};

export const mapUseRegCardStateKidToFormValues = (parent: ParentSaturated, kid: KidSaturated): KidSubform => {
  return {
    firstName: kid.firstName ?? '',
    lastName: kid.lastName ?? '',
    middleName: kid.middleName ?? '',
    birthDate: kid.birthDate ?? '',
    birthPlace: kid.birthPlace ?? '',
    sex: kid.gender ?? Sex.male,
    address: parent.address ?? '',
    mobilePhone: formatPhone(kid.mobilePhone ?? '').replace('+7', '8'),
    email: kid.email ?? '',
    number: `${kid.documentSeries ?? ''}${kid.documentNumber}` ?? '',
    date: kid.documentIssueDate ?? '',
    org: (kid.documentIssuedBy ?? '').replace('выдан', '').trim(),
    addressReg: parent.address ?? '',
    snils: formatSnils(onlyDigitsPipe(kid.snils ?? '')),
  };
};
